/**
 * Cost calculation functions for all deployment options
 */

import { getGrowthMultiplier } from './deploymentHelpers';
import { PROJECTION_YEARS } from './deploymentConstants';

/**
 * Calculate purchased hardware costs over 5 years with YoY growth
 * Hardware purchases are incremental - only buy what's needed beyond current capacity
 */
export const calculatePurchasedHardwareCost = ({
    requiredThroughput,
    selectedHardware,
    yoyGrowthPercent,
    onPremPercent = 100
}) => {
    const adjustedThroughput = (requiredThroughput * onPremPercent) / 100;

    // Calculate required servers for each year based on growth
    const yearlyServers = [];
    for (let year = 1; year <= PROJECTION_YEARS; year++) {
        const yearThroughput = adjustedThroughput * getGrowthMultiplier(year, yoyGrowthPercent);
        const servers = Math.ceil(yearThroughput / (selectedHardware.throughputPerGPU * selectedHardware.gpuCount));
        yearlyServers.push(servers);
    }

    // Calculate incremental purchases (only buy additional capacity as needed)
    const yearCosts = [];
    let cumulativeServers = 0;
    for (let year = 1; year <= PROJECTION_YEARS; year++) {
        const neededServers = yearlyServers[year - 1];
        const newServers = Math.max(0, neededServers - cumulativeServers);
        const yearCost = newServers * selectedHardware.purchasePrice;
        yearCosts.push(yearCost);
        cumulativeServers = neededServers;
    }

    return {
        type: 'CapEx',
        requiredServers: yearlyServers,
        year1: yearCosts[0],
        year2: yearCosts[1],
        year3: yearCosts[2],
        year4: yearCosts[3],
        year5: yearCosts[4],
        fiveYearTotal: yearCosts.reduce((a, b) => a + b, 0),
        annualAverage: yearCosts.reduce((a, b) => a + b, 0) / PROJECTION_YEARS
    };
};

/**
 * Calculate cloud rental costs over 5 years with YoY growth
 */
export const calculateCloudRentalCost = ({
    requiredThroughput,
    selectedCloudRental,
    operationsHoursPerDay,
    yoyGrowthPercent,
    cloudPercent = 100
}) => {
    const adjustedThroughput = (requiredThroughput * cloudPercent) / 100;

    const yearCosts = [];
    for (let year = 1; year <= PROJECTION_YEARS; year++) {
        const yearThroughput = adjustedThroughput * getGrowthMultiplier(year, yoyGrowthPercent);
        const requiredInstances = Math.ceil(yearThroughput / (selectedCloudRental.throughputPerGPU * selectedCloudRental.gpuCount));
        const hourlyTotal = requiredInstances * selectedCloudRental.hourlyRate;
        const annualCost = hourlyTotal * operationsHoursPerDay * 365;
        yearCosts.push(annualCost);
    }

    return {
        type: 'OpEx',
        year1: yearCosts[0],
        year2: yearCosts[1],
        year3: yearCosts[2],
        year4: yearCosts[3],
        year5: yearCosts[4],
        fiveYearTotal: yearCosts.reduce((a, b) => a + b, 0),
        annualAverage: yearCosts.reduce((a, b) => a + b, 0) / PROJECTION_YEARS
    };
};

/**
 * Calculate token-based API costs over 5 years with YoY growth
 */
export const calculateTokenUsageCost = ({
    sessionsPerMonth,
    totalInputTokensPerSession,
    totalOutputTokensPerSession,
    selectedTokenModel,
    yoyGrowthPercent,
    tokenPercent = 100
}) => {
    const adjustedSessions = (sessionsPerMonth * tokenPercent) / 100;

    const yearCosts = [];
    for (let year = 1; year <= PROJECTION_YEARS; year++) {
        const yearSessions = adjustedSessions * getGrowthMultiplier(year, yoyGrowthPercent);
        const annualInputCost = (totalInputTokensPerSession * yearSessions * 12 * selectedTokenModel.inputCostPer1MTokens) / 1000000;
        const annualOutputCost = (totalOutputTokensPerSession * yearSessions * 12 * selectedTokenModel.outputCostPer1MTokens) / 1000000;
        const annualCost = annualInputCost + annualOutputCost;
        yearCosts.push(annualCost);
    }

    return {
        type: 'OpEx',
        year1: yearCosts[0],
        year2: yearCosts[1],
        year3: yearCosts[2],
        year4: yearCosts[3],
        year5: yearCosts[4],
        fiveYearTotal: yearCosts.reduce((a, b) => a + b, 0),
        annualAverage: yearCosts.reduce((a, b) => a + b, 0) / PROJECTION_YEARS
    };
};

/**
 * Calculate provisioned throughput (PT/GSU/PTU) costs over 5 years with YoY growth
 * Based on Excel formula F53
 */
export const calculateProvisionedThroughputCost = ({
    sessionsPerMonth,
    totalInputTokensPerSession,
    totalOutputTokensPerSession,
    operationsHoursPerDay,
    selectedPTModel,
    selectedPTCommitment,
    yoyGrowthPercent,
    ptPercent = 100
}) => {
    const adjustedSessions = (sessionsPerMonth * ptPercent) / 100;

    const yearCosts = [];
    for (let year = 1; year <= PROJECTION_YEARS; year++) {
        const yearSessions = adjustedSessions * getGrowthMultiplier(year, yoyGrowthPercent);

        // PT Formula from Excel F53:
        // CEILING(
        //   (Sessions * (InputTokens * BurndownRate + OutputTokens * BurndownRate)) /
        //   (OperationsHours * 3600 * 30)
        // ) / ThroughputPerUnit

        const burndownRate = selectedPTModel.burndownRate;
        const throughputPerUnit = selectedPTModel.throughputPerUnit;

        const requiredThroughput =
            (yearSessions * (totalInputTokensPerSession * burndownRate + totalOutputTokensPerSession * burndownRate)) /
            (operationsHoursPerDay * 3600 * 30);

        const requiredUnits = Math.ceil(requiredThroughput / throughputPerUnit);

        // Get monthly rate for selected commitment period
        const commitment = selectedPTModel.commitments.find(c => c.period === selectedPTCommitment);
        const monthlyRate = commitment ? commitment.monthlyRate : selectedPTModel.commitments[0].monthlyRate;

        const annualCost = requiredUnits * monthlyRate * 12;
        yearCosts.push(annualCost);
    }

    return {
        type: 'OpEx (Reserved)',
        year1: yearCosts[0],
        year2: yearCosts[1],
        year3: yearCosts[2],
        year4: yearCosts[3],
        year5: yearCosts[4],
        fiveYearTotal: yearCosts.reduce((a, b) => a + b, 0),
        annualAverage: yearCosts.reduce((a, b) => a + b, 0) / PROJECTION_YEARS
    };
};

/**
 * Calculate hybrid deployment costs (on-prem + cloud)
 */
export const calculateHybridCost = ({
    hybridOnPremPercent,
    hybridCloudOption,
    // All parameters needed for sub-calculations
    requiredThroughput,
    selectedHardware,
    selectedCloudRental,
    selectedTokenModel,
    selectedPTModel,
    selectedPTCommitment,
    sessionsPerMonth,
    totalInputTokensPerSession,
    totalOutputTokensPerSession,
    operationsHoursPerDay,
    yoyGrowthPercent
}) => {
    const cloudPercent = 100 - hybridOnPremPercent;

    // Calculate on-prem hardware cost
    const onPremCost = calculatePurchasedHardwareCost({
        requiredThroughput,
        selectedHardware,
        yoyGrowthPercent,
        onPremPercent: hybridOnPremPercent
    });

    // Calculate cloud portion based on selected option
    let cloudCost;
    if (hybridCloudOption === 'rental') {
        cloudCost = calculateCloudRentalCost({
            requiredThroughput,
            selectedCloudRental,
            operationsHoursPerDay,
            yoyGrowthPercent,
            cloudPercent
        });
    } else if (hybridCloudOption === 'tokens') {
        cloudCost = calculateTokenUsageCost({
            sessionsPerMonth,
            totalInputTokensPerSession,
            totalOutputTokensPerSession,
            selectedTokenModel,
            yoyGrowthPercent,
            tokenPercent: cloudPercent
        });
    } else { // 'pt'
        cloudCost = calculateProvisionedThroughputCost({
            sessionsPerMonth,
            totalInputTokensPerSession,
            totalOutputTokensPerSession,
            operationsHoursPerDay,
            selectedPTModel,
            selectedPTCommitment,
            yoyGrowthPercent,
            ptPercent: cloudPercent
        });
    }

    return {
        type: 'Hybrid (CapEx + OpEx)',
        onPremCost,
        cloudCost,
        year1: onPremCost.year1 + cloudCost.year1,
        year2: onPremCost.year2 + cloudCost.year2,
        year3: onPremCost.year3 + cloudCost.year3,
        year4: onPremCost.year4 + cloudCost.year4,
        year5: onPremCost.year5 + cloudCost.year5,
        fiveYearTotal: onPremCost.fiveYearTotal + cloudCost.fiveYearTotal
    };
};

/**
 * Determine the best deployment option based on lowest 5-year cost
 */
export const getBestOption = ({
    hardwareCost,
    rentalCost,
    tokenCost,
    ptCost,
    hybridCost,
    enableHybrid
}) => {
    const options = [
        { name: 'Purchased Hardware', cost: hardwareCost.fiveYearTotal, type: hardwareCost.type },
        { name: 'Cloud Rental', cost: rentalCost.fiveYearTotal, type: rentalCost.type },
        { name: 'Token Usage', cost: tokenCost.fiveYearTotal, type: tokenCost.type },
        { name: 'Provisioned Throughput', cost: ptCost.fiveYearTotal, type: ptCost.type }
    ];

    if (enableHybrid && hybridCost) {
        options.push({ name: 'Hybrid Deployment', cost: hybridCost.fiveYearTotal, type: hybridCost.type });
    }

    return options.sort((a, b) => a.cost - b.cost)[0];
};
